import * as geolib from "geolib";
import ConvertUtil from "utils/ConvertUtil.js";
import isNil from "lodash/isNil.js";

export interface Coordinate {
  latitude: number;
  longitude: number;
}

export default class CoordinateUtil {
  static DISTANCE_UNIT: string = "km";
  static METER_ACCURACY: number = 0.01;

  static haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6_371_000; // Earth's radius in meters (same as CoreLocation)
    const toRad = (angle: number): number => (angle * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // distance in meters
  }

  static calculateDistanceBetweenCoordinateInMeter(startCoord: Coordinate, endCoord: Coordinate): number {
    const distance = CoordinateUtil.haversineDistance(
      startCoord.latitude,
      startCoord.longitude,
      endCoord.latitude,
      endCoord.longitude
    );
    return distance;
    // return geolib.getPreciseDistance(startCoord, endCoord, CoordinateUtil.METER_ACCURACY);
  }

  static calculateLinearDistance = (coords: any[], distanceUnit: string): number => {
    if (coords.length < 2) {
      return 0;
    }

    const startCoord = coords[0];
    const endCoord = coords[coords.length - 1];

    if (
      !isNil(startCoord?.latitude) &&
      !isNil(startCoord?.longitude) &&
      !isNil(endCoord?.latitude) &&
      !isNil(endCoord?.longitude)
    ) {
      const linearDistanceInMeters = CoordinateUtil.haversineDistance(
        startCoord.latitude,
        startCoord.longitude,
        endCoord.latitude,
        endCoord.longitude
      );
      // const linearDistanceInMeters = geolib.getPreciseDistance(startCoord, endCoord, CoordinateUtil.METER_ACCURACY);
      const linearDistanceInKM = geolib.convertDistance(linearDistanceInMeters, CoordinateUtil.DISTANCE_UNIT);

      return ConvertUtil.convertDistance(linearDistanceInKM, distanceUnit);
    } else {
      return 0;
    }
  };

  static getDistanceBetweenMostDistantCoordinates(coords: any[], distanceUnit: string): number {
    if (coords.length < 2) {
      return 0;
    }

    let max = 0.0;

    for (let index = 0; index < coords.length; index = index + 5) {
      if (index > 0) {
        const dis = CoordinateUtil.haversineDistance(
          coords[index].latitude,
          coords[index].longitude,
          coords[0].latitude,
          coords[0].longitude
        );
        // const dis = geolib.getDistance(coords[index], coords[0], CoordinateUtil.METER_ACCURACY);
        if (dis > max) {
          max = dis;
        }
      }
    }

    const distanceBetweenMostDistantCoordinatesInKM = geolib.convertDistance(max, CoordinateUtil.DISTANCE_UNIT);

    return ConvertUtil.convertDistance(distanceBetweenMostDistantCoordinatesInKM, distanceUnit);
  }

  static getSquareCornersFromCenter(centerCoord: Coordinate, distanceKm: number = 5): Coordinate[] {
    const {latitude, longitude} = centerCoord;

    // Convert distance from km to meters
    const distanceMeters = distanceKm * 1000;

    // Earth's radius in meters
    const R = 6371000;

    // Convert distance to angular distance (radians)
    const angularDistance = distanceMeters / R;

    // Convert to degrees
    const angularDistanceDegrees = (angularDistance * 180) / Math.PI;

    // Calculate the 4 corner points
    const corners = [
      // North-East corner
      {
        latitude: latitude + angularDistanceDegrees,
        longitude: longitude + angularDistanceDegrees / Math.cos((latitude * Math.PI) / 180)
      },
      // North-West corner
      {
        latitude: latitude + angularDistanceDegrees,
        longitude: longitude - angularDistanceDegrees / Math.cos((latitude * Math.PI) / 180)
      },
      // South-West corner
      {
        latitude: latitude - angularDistanceDegrees,
        longitude: longitude - angularDistanceDegrees / Math.cos((latitude * Math.PI) / 180)
      },
      // South-East corner
      {
        latitude: latitude - angularDistanceDegrees,
        longitude: longitude + angularDistanceDegrees / Math.cos((latitude * Math.PI) / 180)
      }
    ];

    return corners;
  }
}
